/* -------------------------------------------------------------------------- */
/*  @MENTIONS (team chat)                                                     */
/*                                                                            */
/*  A mention is plain text in the message body — "@Priya Sharma" — plus a    */
/*  `mentions` array on the message node that records who each name resolved  */
/*  to:                                                                       */
/*                                                                            */
/*    mentions: [{ id: "<uid>", name: "Priya Sharma" }]                       */
/*                                                                            */
/*  Keeping the display name in the message means an old message still       */
/*  renders correctly after someone is renamed or leaves the org, and a      */
/*  client that doesn't know about mentions still shows a readable           */
/*  "@Priya Sharma". `@everyone` is stored with the sentinel id below.        */
/*                                                                            */
/*  Ported from the mobile app's components/chat/mentions.tsx so both        */
/*  clients resolve and render mentions the same way.                        */
/* -------------------------------------------------------------------------- */
import React from "react";
import { Box, Flex, HStack, Text, VStack, useColorModeValue } from "@chakra-ui/react";
import { ChakraIcon } from "../ChakraIcon";
import { FiUsers } from "react-icons/fi";

export const MENTION_ALL_IDS = ["everyone", "channel"] as const;

export interface MentionTarget {
  id: string;
  name: string;
}

export interface MentionCandidate extends MentionTarget {
  role?: string;
  email?: string;
  /** everyone/channel aren't real members and get a different row icon. */
  isAll?: boolean;
}

/** The partial "@que" the caret currently sits in, or null. */
export interface MentionQuery {
  /** Index of the "@" in the raw input text. */
  start: number;
  /** Index just past the typed query. */
  end: number;
  /** Lowercased text between "@" and the caret. */
  term: string;
}

const MAX_TERM_WORDS = 3;

/**
 * Find the mention being typed immediately before `caret`.
 *
 * Walks back from the caret to the nearest "@" and accepts it only when it
 * starts a word and nothing between it and the caret looks like the end of a
 * mention (a newline, a second "@", or more than three words — names in this
 * org are "First Last", and the slack lets someone type a middle name without
 * the picker vanishing mid-word).
 */
export function findMentionQuery(text: string, caret: number): MentionQuery | null {
  if (!text) return null;
  const upto = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const at = upto.lastIndexOf("@");
  if (at < 0) return null;

  // "@" must start a word, otherwise an email address opens the picker.
  const before = at > 0 ? upto[at - 1] : " ";
  if (!/[\s\n([]/.test(before)) return null;

  const term = upto.slice(at + 1);
  if (/[@\n]/.test(term)) return null;
  if (term.trim().split(/\s+/).filter(Boolean).length > MAX_TERM_WORDS) return null;

  return { start: at, end: upto.length, term: term.toLowerCase() };
}

/** Candidates whose name or email matches the typed term, best match first. */
export function filterMentionCandidates(
  candidates: MentionCandidate[],
  term: string,
  limit = 8,
): MentionCandidate[] {
  const t = (term || "").trim().toLowerCase();
  const scored = candidates
    .map((c) => {
      const name = (c.name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      if (!t) return { c, score: 0 };
      if (name.startsWith(t)) return { c, score: 0 };
      // Match on any word so "@sharma" finds "Priya Sharma".
      if (name.split(/\s+/).some((w) => w.startsWith(t))) return { c, score: 1 };
      if (name.includes(t)) return { c, score: 2 };
      if (email.startsWith(t)) return { c, score: 3 };
      return { c, score: -1 };
    })
    .filter((s) => s.score >= 0);

  // "All Users" always leads, whatever the alphabet says.
  scored.sort(
    (a, b) =>
      Number(!!b.c.isAll) - Number(!!a.c.isAll) ||
      a.score - b.score ||
      a.c.name.localeCompare(b.c.name),
  );
  return scored.slice(0, limit).map((s) => s.c);
}

/**
 * Splice the picked name into the draft, returning the new text and where the
 * caret should land. The trailing space is what closes the picker: the query
 * parser stops at it, so the next keystroke is ordinary text again.
 */
export function applyMention(
  text: string,
  q: MentionQuery,
  candidate: MentionCandidate,
): { text: string; caret: number } {
  const inserted = `@${candidate.name} `;
  const next = text.slice(0, q.start) + inserted + text.slice(q.end);
  return { text: next, caret: q.start + inserted.length };
}

/** Everyone named in `text` who is a known candidate, deduped by id. */
export function collectMentions(
  text: string,
  candidates: MentionCandidate[],
): MentionTarget[] {
  if (!text) return [];
  const found = new Map<string, MentionTarget>();
  // Longest name first so "@Priya Sharma" doesn't match a "@Priya" who also
  // exists and leave " Sharma" behind as plain text.
  const sorted = [...candidates].sort((a, b) => b.name.length - a.name.length);
  for (const c of sorted) {
    if (!c?.name || !c?.id) continue;
    if (text.toLowerCase().includes(`@${c.name.toLowerCase()}`)) {
      if (!found.has(c.id)) found.set(c.id, { id: c.id, name: c.name });
    }
  }
  return [...found.values()];
}

/** Did this message mention me (directly or via @everyone/@channel)? */
export function mentionsUser(
  mentions: MentionTarget[] | undefined | null,
  uid: string | null | undefined,
): boolean {
  if (!mentions?.length) return false;
  return mentions.some(
    (m) =>
      m?.id === uid || MENTION_ALL_IDS.includes(m?.id as (typeof MENTION_ALL_IDS)[number]),
  );
}

/**
 * Split a message body into plain runs and mention tokens so the renderer can
 * style the tokens. Only names carried on the message are highlighted — a bare
 * "@somebody" someone typed by hand stays plain text.
 */
export function splitMentions(
  text: string,
  mentions: MentionTarget[] | undefined | null,
): Array<{ text: string; mention?: MentionTarget }> {
  if (!text) return [];
  const names = (mentions || [])
    .filter((m) => m?.name)
    .sort((a, b) => b.name.length - a.name.length);
  if (!names.length) return [{ text }];

  const out: Array<{ text: string; mention?: MentionTarget }> = [];
  const lower = text.toLowerCase();
  let i = 0;
  let plainFrom = 0;

  while (i < text.length) {
    if (text[i] === "@") {
      const at = i;
      const hit = names.find((m) => lower.startsWith(`@${m.name.toLowerCase()}`, at));
      if (hit) {
        if (i > plainFrom) out.push({ text: text.slice(plainFrom, i) });
        const len = hit.name.length + 1;
        out.push({ text: text.slice(i, i + len), mention: hit });
        i += len;
        plainFrom = i;
        continue;
      }
    }
    i++;
  }
  if (plainFrom < text.length) out.push({ text: text.slice(plainFrom) });
  return out;
}

/* -------------------------------------------------------------------------- */
/*  PICKER                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Suggestion list shown directly above the message input, sized to its
 * content rather than stretched to the input's full width — a small floating
 * card near the caret, the way WhatsApp's picker reads, not a full-width bar.
 * `onPick` fires on mousedown rather than click so it beats the input's own
 * blur — a plain click would close the picker (blur) before the click ever
 * registers.
 */
export const MentionPicker = React.memo(function MentionPicker({
  candidates,
  onPick,
}: {
  candidates: MentionCandidate[];
  onPick: (c: MentionCandidate) => void;
}) {
  const cardBg = useColorModeValue("white", "#1A1C1F");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const avatarBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const avatarColor = useColorModeValue("zs.accent", "zs.accent");

  if (!candidates.length) return null;
  return (
    <Box
      position="absolute"
      bottom="100%"
      left={0}
      mb={2}
      w="fit-content"
      minW="180px"
      maxW="260px"
      maxH="220px"
      overflowY="auto"
      bg={cardBg}
      borderRadius="12px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="lg"
      zIndex={30}
    >
      <VStack align="stretch" spacing={0} py={1}>
        {candidates.map((c) => (
          <HStack
            key={c.id}
            spacing={2.5}
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: hoverBg }}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(c);
            }}
          >
            <Flex
              w="26px"
              h="26px"
              borderRadius="full"
              align="center"
              justify="center"
              flexShrink={0}
              bg={c.isAll ? "zs.accent" : avatarBg}
            >
              {c.isAll ? (
                <ChakraIcon as={FiUsers} boxSize="13px" color="white" />
              ) : (
                <Text fontSize="12px" fontWeight="700" color={avatarColor}>
                  {(c.name || "?").trim().charAt(0).toUpperCase()}
                </Text>
              )}
            </Flex>
            <Text fontSize="14px" fontWeight="600" color={textColor} isTruncated>
              {c.name}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
});
